import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type { PipelineStatus } from './usePipelines';

export interface ProjectJob {
  id: string;
  project_id: string;
  job_id: string;
  company_id: string;
  added_at: string;
  job: {
    id: string;
    job_number: string;
    customer_name: string;
    customer_id: string | null;
    status: string;
    invoice_total: number | null;
    scheduled_date: string | null;
  };
}

export interface Project {
  id: string;
  company_id: string;
  pipeline_id: string;
  current_status_id: string | null;
  name: string;
  notes: string | null;
  project_metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  current_status: PipelineStatus | null;
  project_jobs: ProjectJob[];
}

// -----------------------------------------------------------------------
// Queries
// -----------------------------------------------------------------------

export function useProjects(pipelineId?: string) {
  return useQuery({
    queryKey: ['projects', pipelineId],
    queryFn: async (): Promise<Project[]> => {
      let q = supabase
        .from('projects')
        .select(`
          id, company_id, pipeline_id, current_status_id, name, notes,
          project_metadata, created_by, created_at, updated_at,
          current_status:pipeline_statuses!projects_current_status_id_fkey (
            id, pipeline_id, company_id, name, position, color, is_terminal
          ),
          project_jobs (
            id, project_id, job_id, company_id, added_at,
            job:jobs!project_jobs_job_id_fkey (
              id, job_number, customer_name, customer_id, status, invoice_total, scheduled_date
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (pipelineId) {
        q = q.eq('pipeline_id', pipelineId);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as Project[];
    },
    staleTime: 30 * 1000,
  });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: async (): Promise<Project | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('projects')
        .select(`
          id, company_id, pipeline_id, current_status_id, name, notes,
          project_metadata, created_by, created_at, updated_at,
          current_status:pipeline_statuses!projects_current_status_id_fkey (
            id, pipeline_id, company_id, name, position, color, is_terminal
          ),
          project_jobs (
            id, project_id, job_id, company_id, added_at,
            job:jobs!project_jobs_job_id_fkey (
              id, job_number, customer_name, customer_id, status, invoice_total, scheduled_date
            )
          )
        `)
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as unknown as Project;
    },
    enabled: !!id,
  });
}

// -----------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pipelineId,
      statusId,
      name,
      notes,
      jobId,
    }: {
      pipelineId: string;
      statusId: string | null;
      name: string;
      notes?: string;
      jobId?: string;
    }) => {
      const { data: project, error } = await supabase
        .from('projects')
        .insert({
          pipeline_id: pipelineId,
          current_status_id: statusId,
          name,
          notes: notes ?? null,
        })
        .select()
        .single();
      if (error) throw error;

      // Optionally link a job; on failure clean up the orphaned project
      if (jobId) {
        const { error: jErr } = await supabase
          .from('project_jobs')
          .insert({ project_id: project.id, job_id: jobId });
        if (jErr) {
          await supabase.from('projects').delete().eq('id', project.id);
          throw jErr;
        }
      }

      return project;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Project created' });
    },
    onError: (e: Error) => toast({ title: 'Failed to create project', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<Project, 'name' | 'notes' | 'project_metadata'>>;
    }) => {
      const { error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['project', vars.id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Project updated' });
    },
    onError: (e: Error) => toast({ title: 'Failed to update project', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdateProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, statusId }: { id: string; statusId: string }) => {
      const { error } = await supabase
        .from('projects')
        .update({ current_status_id: statusId })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['project', vars.id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e: Error) => toast({ title: 'Failed to move project', description: e.message, variant: 'destructive' }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projects').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Project deleted' });
    },
    onError: (e: Error) => toast({ title: 'Failed to delete project', description: e.message, variant: 'destructive' }),
  });
}

export function useAddJobToProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, jobId }: { projectId: string; jobId: string }) => {
      const { error } = await supabase
        .from('project_jobs')
        .insert({ project_id: projectId, job_id: jobId });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['project', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Job added to project' });
    },
    onError: (e: Error) => toast({ title: 'Failed to add job', description: e.message, variant: 'destructive' }),
  });
}

export function useRemoveJobFromProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, jobId }: { projectId: string; jobId: string }) => {
      const { error } = await supabase
        .from('project_jobs')
        .delete()
        .eq('project_id', projectId)
        .eq('job_id', jobId);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['project', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Job removed from project' });
    },
    onError: (e: Error) => toast({ title: 'Failed to remove job', description: e.message, variant: 'destructive' }),
  });
}

// Lightweight hook for just the project IDs a job belongs to (used in job detail)
export function useJobProjects(jobId: string | null) {
  return useQuery({
    queryKey: ['job-projects', jobId],
    queryFn: async () => {
      if (!jobId) return [];
      const { data, error } = await supabase
        .from('project_jobs')
        .select(`
          project_id,
          project:projects!project_jobs_project_id_fkey (
            id, name, current_status_id,
            current_status:pipeline_statuses!projects_current_status_id_fkey (name, color)
          )
        `)
        .eq('job_id', jobId);
      if (error) throw error;
      return (data ?? []).map(r => r.project) as Array<{
        id: string;
        name: string;
        current_status_id: string | null;
        current_status: { name: string; color: string } | null;
      }>;
    },
    enabled: !!jobId,
    staleTime: 30 * 1000,
  });
}
